/**
 * Our Team Page - Public page displaying coaches and founders for a club
 * 
 * Features:
 * - Displays club coaches and founders with photos, names, and bios
 * - Expandable/collapsible bio sections (inline popup style)
 * - Accessible to all users (public page)
 * - Filtered by club from domain/subdomain context
 * - Security: Only shows data for the current club
 */

import React, { useState, useEffect, useRef } from 'react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import LoadingState from '../components/LoadingState';
import { useTenant } from '../contexts/TenantContext';
import apiService from '../services/api';
import styles from './OurTeam.module.css';
import Card, { CardContent } from '../components/Card';

/**
 * Team Member Card Component
 * Displays individual coach/founder with expandable bio
 */
const TeamMemberCard = ({ member }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const expandedRef = useRef(null);

  // Close expanded bio when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedRef.current && !expandedRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isExpanded]);

  const { clubColors } = useTenant();

  // Determine photo src: if backend base URL is configured, prefix relative /uploads paths
  const photoSrc = member.photo_url && member.photo_url.startsWith('/uploads') && apiService.baseURL
    ? `${apiService.baseURL}${member.photo_url}`
    : member.photo_url;

  const roleLabel = member.role === 'coach'
    ? 'Coach'
    : member.role === 'founder'
    ? 'Founder'
    : member.role === 'club_admin'
    ? 'Club Admin'
    : member.role === 'branch_admin'
    ? 'Branch Admin'
    : member.role;

  // Truncate bio for collapsed view (first 150 characters or first sentence)
  const fullBio = member.bio || '';
  const truncatedBio = fullBio.length > 150
    ? fullBio.substring(0, 150).split('.')[0] + '...'
    : fullBio;

  return (
    <Card level={2} className={styles.memberCard}>
      {/* Photo Section as top content (no padding) */}
      <CardContent noPadding={true} className={styles.photoWrapper}>
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={`${member.first_name} ${member.last_name}`}
            className={styles.photo}
          />
        ) : (
          <div className={styles.photoPlaceholder} style={{ background: clubColors?.primary || '' }}>
            <span>{member.first_name?.charAt(0)}{member.last_name?.charAt(0)}</span>
          </div>
        )}
        <div className={styles.roleBadge} style={{ background: clubColors?.secondary || undefined }}>
          {roleLabel}
        </div>
      </CardContent>

      {/* Content Section */}
      <CardContent>
        <h3 className={styles.name}>
          {member.first_name} {member.last_name}
        </h3>

        {/* Bio with expand/collapse */}
        <div className={styles.bioSection}>
          <p className={styles.bioText}>
            {truncatedBio}
          </p>

          {fullBio.length > 150 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.expandButton}
              aria-label={isExpanded ? 'Collapse bio' : 'Expand bio'}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}

          {/* Expanded bio popup (inline style) */}
          {isExpanded && fullBio.length > 150 && (
            <div
              className={styles.expandedBioPopup}
              ref={expandedRef}
              role="region"
              aria-label={`Full bio for ${member.first_name} ${member.last_name}`}
            >
              <div className={styles.expandedBioContent}>
                <button
                  className={styles.closeButton}
                  onClick={() => setIsExpanded(false)}
                  aria-label="Close bio"
                >
                  ✕
                </button>
                <h4 className={styles.expandedBioTitle}>
                  {member.first_name} {member.last_name}
                </h4>
                <p className={styles.expandedBioText}>
                  {fullBio}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Main OurTeam Page Component
 */
const OurTeam = () => {
  const { club_id, clubDisplayName, loading: tenantLoading } = useTenant();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch team members (coaches and founders) for the club
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!club_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching team members for club_id:', club_id);

        // Call endpoint to fetch coaches and founders for this club
        // Tenant is detected from Host header via middleware (subdomain/domain)
        // The endpoint returns { data: [...], count: ..., club_id: ... }
        const response = await apiService.getTeamMembers(['coach', 'founder', 'club_admin', 'branch_admin']);

        console.log('Team members response:', response);
        setTeamMembers(response.data || []);
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Unable to load team members. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [club_id]);

  // Handle loading states
  if (tenantLoading) {
    return <LoadingState message="Loading club information..." />;
  }

  if (loading) {
    return <LoadingState message="Loading team members..." />;
  }

  // Separate coaches, founders and admins
  const coaches = teamMembers.filter(m => m.role === 'coach');
  const founders = teamMembers.filter(m => m.role === 'founder');
  const admins = teamMembers.filter(m => ['club_admin', 'branch_admin'].includes(m.role));

  return (
    <PageLayout>
      <PageHeader
        title="Our Team"
        subtitle={clubDisplayName ? `Meet the ${clubDisplayName} coaching staff and leadership` : 'Meet our coaching staff and leadership'}
      />

      {/* Error state */}
      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}

      {/* No members */}
      {teamMembers.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p>No team members to display yet.</p>
        </div>
      )}

      {/* Founders section */}
      {founders.length > 0 && (
        <Section>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Founders</h2>
            <div className={styles.membersGrid}>
              {founders.map((member) => (
                <TeamMemberCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Administration section */}
      {admins.length > 0 && (
        <Section>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Administration</h2>
            <div className={styles.membersGrid}>
              {admins.map((member) => (
                <TeamMemberCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Coaches section */}
      {coaches.length > 0 && (
        <Section>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Coaching Staff</h2>
            <div className={styles.membersGrid}>
              {coaches.map((member) => (
                <TeamMemberCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </Section>
      )}
    </PageLayout>
  );
};

export default OurTeam;
